import React, { useState } from "react";
import AmsterModal from "../../../components/AmsterModal";
import {
  Box,
  Typography,
  Container,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import experiencesData from "../../../constants/experiences";

const Experiences = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [swiper, setSwiper] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Use the centralized experiences data, but map to the minimal fields needed for the slider
  const experiences = experiencesData.map((exp) => ({
    id: exp.id,
    title: exp.name,
    image: exp.image,
  }));

  return (
    <Box
      sx={{
        py: { xs: 12 },
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ mb: 0 }}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item size={{ xs: 12, md: 6 }}>
              {/* Category Label */}
              <Typography
                data-aos="fade-up"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: "Monument-Private",
                  fontWeight: 500,
                  letterSpacing: 2,
                  fontSize: "0.75rem",
                  mb: 2,
                }}
              >
                EXPERIENCES
              </Typography>

              {/* Main Heading */}
              <Typography
                variant="h2"
                component="h2"
                data-aos="fade-up"
                data-aos-delay="200"
                sx={{
                  fontFamily: "Monument-Private",
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  fontWeight: "bold",
                  lineHeight: 1.1,
                  color: theme.palette.text.primary,
                }}
              >
                Things to do in Goa
              </Typography>
            </Grid>

            {/* Description on the right */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body1"
                data-aos="fade-up"
                data-aos-delay="400"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  mt: { xs: 0, md: 4 },
                  fontWeight: 400,
                }}
              >
                Explore authentic Goan experiences—from water sports and spice plantations to heritage walks and culinary adventures. We help you discover the best of Goa, curated for every kind of traveler.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Slideshow Container */}
        <Box sx={{ position: "relative", py: 4, overflow: "hidden" }}>
          <Swiper
            data-aos="fade-up"
            data-aos-delay="600"
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={isMobile ? 1 : 3}
            navigation={{
              prevEl: ".custom-prev",
              nextEl: ".custom-next",
            }}
            pagination={false}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            loop={true}
            onSwiper={setSwiper}
            centeredSlides={false}
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 15,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
            }}
            style={{
              paddingBottom: "50px",
              paddingTop: "10px",
            }}
          >
            {experiences.map((experience) => (
              <SwiperSlide key={experience.id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "350px",
                      borderRadius: "50px",
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      zIndex: 1,
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                        zIndex: 10,
                      },
                    }}
                    onClick={() => setOpenModal(true)}
                  >
                    {/* Background Image */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${experience.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />

                    {/* Gradient Overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)",
                      }}
                    />

                    {/* Title */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 3,
                        color: "white",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontFamily: "Monument-Private",
                          fontWeight: 600,
                          fontSize: "1.25rem",
                          textAlign: "center",
                        }}
                      >
                        {experience.title}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </SwiperSlide>
            ))}

            {/* Amster Modal */}
            <AmsterModal open={openModal} onClose={() => setOpenModal(false)} />
          </Swiper>
        </Box>

        {/* See All Villas Button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Link to="/experiences" style={{ textDecoration: "none" }}>
            <Button
              variant="outlined"
              data-aos="fade-up"
              data-aos-delay="800"
              endIcon={<ArrowForward sx={{ fontSize: "1rem" }} />}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                px: 4,
                py: 1.5,
                borderRadius: 6,
                borderColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
                fontSize: "1rem",
                borderWidth: 1,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: theme.palette.text.primary,
                  color: "white",
                  borderColor: theme.palette.text.primary,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              See all Experiences
            </Button>
          </Link>
        </Box>

        {/* Custom Navigation Controls */}
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <IconButton
            className="custom-prev"
            sx={{
              width: 100,
              height: 60,
              border: `1px solid ${theme.palette.text.primary}`,
              borderRadius: "50px",
              color: theme.palette.text.primary,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: theme.palette.text.primary,
                color: "white",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
              },
            }}
          >
            <ArrowBack />
          </IconButton>

          <IconButton
            className="custom-next"
            sx={{
              width: 100,
              height: 60,
              border: `1px solid ${theme.palette.text.primary}`,
              borderRadius: "50px",
              color: theme.palette.text.primary,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: theme.palette.text.primary,
                color: "white",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
              },
            }}
          >
            <ArrowForward />
          </IconButton>
        </Box> */}

        {/* Custom Styles for Swiper */}
        <style jsx global>{`
          .swiper-pagination {
            bottom: 20px !important;
          }

          .swiper-pagination-bullet {
            width: 12px !important;
            height: 12px !important;
            background: ${theme.palette.text.secondary} !important;
            opacity: 0.3 !important;
            margin: 0 6px !important;
            border-radius: 50% !important;
            transition: all 0.3s ease !important;
          }

          .swiper-pagination-bullet-active {
            background: ${theme.palette.primary.main} !important;
            opacity: 1 !important;
            transform: scale(1.2) !important;
          }

          .swiper-slide {
            height: auto !important;
            overflow: visible !important;
          }

          .swiper-wrapper {
            align-items: stretch !important;
          }

          .swiper {
            overflow: hidden !important;
          }
        `}</style>
      </Container>
    </Box>
  );
};

export default Experiences;
